import { Button, TextField } from '@blueprinthq/joy'
import { Box, Flex, HStack, Link, Text, VStack } from '@chakra-ui/react'
import { datadogLogs } from '@datadog/browser-logs'
import { Navbar } from '@handlers/signup-v2/components/navbar'
import { SocialAuthButtons } from '@handlers/signup-v2/components/social-auth-buttons'
import SocialProofFooter from '@handlers/signup-v2/components/social-proof-footer'
import { getCookie } from '@utilities'
import { useStoreActions, useStoreState } from 'easy-peasy'
import { Field, type FieldProps, Form, Formik } from 'formik'
import { AnimatePresence, motion } from 'framer-motion'
import qs from 'query-string'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useQueryClient } from 'react-query'
import {
  Redirect,
  Link as RouterLink,
  useHistory,
  useLocation
} from 'react-router-dom'
import * as Yup from 'yup'
import { Terms } from './terms'
import { Testimonial } from './testimonial'

const MotionContainer = motion(Box)

interface FormValues {
  email: string
  password: string
}

const LoginSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string().password().required('Required')
})

const Login = () => {
  const location = useLocation<any>()
  const history = useHistory()
  const isExtension = location.pathname.includes('/extension')
  const isWidget = location.pathname.includes('/widget')
  const isMiniWidget = location.pathname.includes('/mini-widget')

  const authLogin = useStoreActions((actions: any) => actions.auth.login)
  const authSocial = useStoreActions((actions: any) => actions.auth.authSocial)
  const widgetLogin = useStoreActions(
    (actions: any) => actions.auth.widgetLogin
  )
  const resumeSession = useStoreActions(
    (actions: any) => actions.auth.resumeSession
  )
  const { isAuthenticated } = useStoreState((state: any) => state.auth)
  const queryClient = useQueryClient()
  const [loginError, setLoginError] = useState<string | null>(null)
  const { next: nextUrl, ...searchQuery } = qs.parse(location.search)
  const queryParams = qs.stringify(searchQuery)
  const redirectUrl = isExtension
    ? '/extension'
    : isWidget
      ? '/widget'
      : isMiniWidget
        ? '/mini-widget'
        : nextUrl
          ? `${nextUrl}?${queryParams}`
          : `/dashboard?${queryParams}`

  const heroImageUrl =
    'https://res.cloudinary.com/hellojoy/image/upload/v1748885549/magnifics_upscale-5HXUHBAlvxJ9nB5PUEng-bpbotextension1_45677_httpss.mj.run0VUc87ge9ks_httpss.mj.runU_d54e814c-473b-4254-9816-513681fb260f_1_1_o53qn7.jpg'

  const initialValues = {
    email: (location.state && location.state.email) || searchQuery?.email || '',
    password: ''
  }

  const onNavbarClicked = () => {
    if (isExtension || isWidget || isMiniWidget) return
    window.location.href = `${process.env.REACT_APP_WWW_URL}`
  }

  const isSupportEnabled =
    //@ts-ignore
    window.Intercom &&
    process.env.REACT_APP_HIDE_INTERCOM !== 'true' &&
    process.env.REACT_APP_ENV !== 'development'

  const submitLogin = async (
    credentials: FormValues,
    isWidget: boolean = false
  ) => {
    try {
      // Triggers cognito login
      if (isWidget) {
        await widgetLogin(credentials)
      } else {
        await authLogin(credentials)
      }
      // Remove cache from any previous sessions
      await queryClient.clear()
      // Redirect to url if login and account are valid
      history.push(redirectUrl)
    } catch (error) {
      setLoginError(
        error instanceof Error ? error.message : 'An unknown error occurred'
      )
    }
  }

  const handleOAuth = async (provider: 'google' | 'microsoft') => {
    await authSocial(provider)
  }

  const isSocialAuthFlagEnabled =
    localStorage.getItem('isSocialAuthFlagEnabled') === 'true'

  useEffect(() => {
    const handleWidgetAuthentication = (event: any) => {
      submitLogin(event.data.authTokens, true)
    }

    const messageHandler = (event: any) => {
      if (event.data.type === 'BP_AUTHENTICATE')
        handleWidgetAuthentication(event)
    }

    window.addEventListener('message', messageHandler)

    return () => {
      window.removeEventListener('message', messageHandler)
    }
  }, [])

  useEffect(() => {
    if (isExtension) {
      const interval = setInterval(() => {
        const ssoAuthToken = getCookie('ssoAccessToken')
        if (ssoAuthToken) {
          clearInterval(interval)
          datadogLogs.logger.info(
            '[Auth Debug - Login] Extension SSO Resume Session'
          )
          resumeSession()
        }
      }, 1000)

      return () => {
        clearInterval(interval)
      }
    }
  }, [])

  if (isAuthenticated) {
    return (
      <Redirect
        to={
          isExtension
            ? '/extension'
            : isWidget
              ? '/widget'
              : isMiniWidget
                ? '/mini-widget'
                : '/'
        }
      />
    )
  }

  return (
    <>
      <Helmet>
        <meta name="title" content="Blueprint for Therapists — Login" />
        <meta
          name="description"
          content="Sign-in to your clinician account. Blueprint is the AI Assistant for Therapists — automate progress notes, draft smart treatments plans, and surface actionable insights before, during and after every session."
        />
      </Helmet>
      <Flex
        w="100%"
        id="bp-extension-loaded"
        h="100%"
        p={4}
        gap={{ base: 0 }}
        bgColor="#F6F7FE"
        _before={{
          content: '""',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0,
          animation: 'none'
        }}
        sx={{
          '@keyframes fadeIn': {
            '0%': { opacity: 0 },
            '100%': { opacity: 1 }
          }
        }}
      >
        <Flex
          h="100%"
          flexDirection="column"
          width={{ base: '100%', md: '50%' }}
          transition="all 500ms cubic-bezier(0.920, 0.005, 0.145, 1.000)"
          position="relative"
        >
          <Box
            onClick={onNavbarClicked}
            cursor={
              isExtension || isWidget || isMiniWidget ? 'default' : 'pointer'
            }
          >
            <Navbar onBack={null} onSkip={null} />
          </Box>
          <Flex flex="1" justifyContent={{ base: 'flex-start', md: 'center' }}>
            <Formik<FormValues>
              initialValues={initialValues}
              validate={() => setLoginError(null)}
              validationSchema={LoginSchema}
              onSubmit={async values => {
                try {
                  submitLogin({
                    email: values.email,
                    password: values.password
                  })
                } catch (error) {
                  setLoginError(
                    error instanceof Error
                      ? error.message
                      : 'An unknown error occurred'
                  )
                }
              }}
              validateOnBlur={false}
              validateOnChange={false}
            >
              {({ values, errors, touched, isSubmitting }) => (
                <Form
                  noValidate
                  style={{
                    overflow: 'hidden',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center'
                  }}
                >
                  <Flex
                    maxW={{ base: '100%', md: 'md' }}
                    flexDirection="column"
                    px={6}
                    py={4}
                    flex="1"
                    justifyContent={{ base: 'space-between', md: 'center' }}
                    position="relative"
                  >
                    <VStack
                      flex="1"
                      position={{ base: 'static', md: 'relative' }}
                    >
                      <Flex
                        flex={{ base: '2', md: '1' }}
                        flexDir="column"
                        w="100%"
                        justifyContent={{ base: 'flex-start', md: 'center' }}
                        mt={{ base: '0', md: '-12' }}
                      >
                        <Box textAlign="center">
                          <Text
                            fontSize="24px"
                            fontFamily="TofinoPersonal"
                            fontWeight="440"
                          >
                            Log in to your
                            <br />
                            <Text
                              as="span"
                              color="primary"
                              fontSize="24px"
                              fontFamily="TofinoPersonal"
                              fontWeight="440"
                            >
                              Clinician account
                            </Text>
                          </Text>
                        </Box>
                        <Box mt={{ base: 2, md: 6 }}>
                          {loginError && (
                            <Text color="error" mb={4}>
                              {loginError}
                            </Text>
                          )}
                          <Field name="email">
                            {({ field }: FieldProps) => {
                              const isInvalid = !!(
                                errors.email && touched.email
                              )
                              return (
                                <Box mb={isInvalid ? 8 : 0}>
                                  <TextField
                                    {...field}
                                    type="email"
                                    w="100%"
                                    label="Email *"
                                    isInvalid={isInvalid}
                                    errorText={errors.email}
                                    bg="white"
                                    activeLabelStyle={{
                                      bgGradient: 'linear(to-t, white, #F6F7FE)'
                                    }}
                                    autoFocus
                                  />
                                </Box>
                              )
                            }}
                          </Field>
                          <Field name="password">
                            {({ field }: FieldProps) => {
                              const isInvalid = !!(
                                errors.password && touched.password
                              )
                              return (
                                <Box mt={4} mb={isInvalid ? 8 : 0}>
                                  <TextField
                                    {...field}
                                    type="password"
                                    w="100%"
                                    label="Password *"
                                    autoFocus={Boolean(
                                      location.state && location.state.email
                                    )}
                                    isInvalid={isInvalid}
                                    errorText={errors.password}
                                    bg="white"
                                    activeLabelStyle={{
                                      bgGradient: 'linear(to-t, white, #F6F7FE)'
                                    }}
                                  />
                                </Box>
                              )
                            }}
                          </Field>

                          {!isExtension && !isWidget && !isMiniWidget && (
                            <HStack
                              justifyContent="space-between"
                              w="100%"
                              mt={3}
                            >
                              <Link
                                color="primary"
                                as={RouterLink}
                                to={{
                                  pathname: '/forgot-password',
                                  state: {
                                    email: values.email
                                  }
                                }}
                                fontSize="12px"
                              >
                                Forgot password?
                              </Link>
                              <Text align="center" fontSize="12px">
                                Are you a client?{' '}
                                <Link
                                  color="primary"
                                  href={`${process.env.REACT_APP_CLIENT_APP_URL}/login`}
                                >
                                  Log in
                                </Link>
                              </Text>
                            </HStack>
                          )}

                          <Button
                            size="lg"
                            isFullWidth
                            mt={4}
                            isLoading={isSubmitting}
                            type="submit"
                          >
                            Log in
                          </Button>

                          {isSocialAuthFlagEnabled && (
                            <SocialAuthButtons
                              mt={4}
                              onGoogleAuth={() => handleOAuth('google')}
                              onMicrosoftAuth={() => handleOAuth('microsoft')}
                              isLoading={isSubmitting}
                            />
                          )}

                          {!isWidget &&
                            !isMiniWidget &&
                            (isExtension ? (
                              <Button
                                size="lg"
                                isFullWidth
                                variant="outline"
                                borderColor="#373C4123"
                                _hover={{
                                  textDecoration: 'none',
                                  bg: 'rgba(45, 84, 232, 0.15)'
                                }}
                                my={4}
                                onClick={() => {
                                  const ssoUrl = `${window.location.origin.replace(
                                    'extension.',
                                    ''
                                  )}/sso${location.search}`
                                  window.open(ssoUrl)
                                }}
                              >
                                Log in with Single Sign-On (SSO)
                              </Button>
                            ) : (
                              <Button
                                size="lg"
                                isFullWidth
                                variant="outline"
                                borderColor="#373C4123"
                                _hover={{
                                  textDecoration: 'none',
                                  bg: 'rgb(238 240 253)'
                                }}
                                my={4}
                                onClick={() => {
                                  window.location.href = `/sso${location.search}`
                                }}
                              >
                                Log in with Single Sign-On (SSO)
                              </Button>
                            ))}
                          {isSupportEnabled && (
                            <Text align="center" fontSize="12px" mt={2}>
                              Having trouble logging in?{' '}
                              <Link
                                color="primary"
                                onClick={() => {
                                  //@ts-ignore
                                  window.Intercom(
                                    'showNewMessage',
                                    'Hi, I am having issues logging in.'
                                  )
                                }}
                              >
                                Contact support
                              </Link>
                            </Text>
                          )}
                          {!isExtension && !isWidget && !isMiniWidget && (
                            <Text align="center" fontSize="12px" mt={2}>
                              Don't have an account?{' '}
                              <Link color="primary" href="/signup">
                                Sign up for free
                              </Link>
                            </Text>
                          )}
                          <Terms
                            display={{ base: 'none', md: 'block' }}
                            mt={2}
                          />
                        </Box>
                      </Flex>
                      <SocialProofFooter
                        minCollapsableHeight={
                          isSocialAuthFlagEnabled ? '940px' : '850px'
                        }
                        showTerms={true}
                        termsComponent={
                          <Terms
                            display={{ base: 'block', md: 'none' }}
                            mt={2}
                          />
                        }
                      />
                    </VStack>
                  </Flex>
                </Form>
              )}
            </Formik>
          </Flex>
        </Flex>
        <Box
          width={{ base: '0%', md: '50%' }}
          bg="gray.100"
          display="flex"
          borderRadius="8px"
          justifyContent="center"
          alignItems="center"
          overflow="hidden"
          position="relative"
        >
          <AnimatePresence>
            <MotionContainer
              key={heroImageUrl}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                opacity: { duration: 0.5, ease: 'easeInOut' },
                exit: { duration: 0.1, ease: 'easeInOut' }
              }}
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                background: `url(${heroImageUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            ></MotionContainer>
          </AnimatePresence>

          <Box
            w="100%"
            position="absolute"
            top="50%"
            transform="translateY(-50%)"
          >
            <Testimonial />
          </Box>
        </Box>
      </Flex>
    </>
  )
}

export default Login
